import React, { useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";

const PhonePeFailedPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const transactionId = searchParams.get("transactionId");
  const reason = searchParams.get("reason");
  const error = searchParams.get("error");

  useEffect(() => {
    // Clean up temporary data
    localStorage.removeItem("tempOrderData");
    localStorage.removeItem("tempPhonePeTransaction");

    // Show error message
    if (reason) {
      toast.error(`Payment failed: ${decodeURIComponent(reason)}`);
    } else if (error) {
      toast.error(`Payment error: ${decodeURIComponent(error)}`);
    } else {
      toast.error("Payment was cancelled or failed");
    }
  }, [reason, error]);

  const handleRetryPayment = () => {
    // Redirect back to checkout page
    navigate("/checkout");
  };

  const handleContinueShopping = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-red-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-orange-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

        <div className="max-w-lg w-full relative z-10">
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/40 p-8 sm:p-10 text-center transform transition-all hover:scale-[1.01] duration-300">
            <div className="flex justify-center mb-8 relative">
              <div className="absolute inset-0 bg-red-100 rounded-full scale-150 blur-xl opacity-50 animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-red-500 to-red-600 rounded-full p-4 shadow-lg shadow-red-200">
                <svg
                  className="w-12 h-12 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </div>
            </div>

            <h2 className="text-3xl font-extrabold text-gray-900 mb-3 tracking-tight">
              Payment Failed
            </h2>

            <p className="text-gray-500 mb-8 text-lg">
              We couldn't process your payment. Don't worry, no charges were made.
            </p>

            {transactionId && (
              <div className="bg-gray-50/80 backdrop-blur-sm border border-gray-100 rounded-2xl p-5 mb-8 text-left shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="mt-0.5">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-800">
                      <span className="font-semibold text-gray-500 block mb-1">Transaction ID</span>
                      <span className="font-mono bg-white px-2 py-1 rounded border border-gray-100 text-xs">{transactionId}</span>
                    </p>
                    {reason && (
                      <p className="text-sm text-red-600 mt-3 font-medium bg-red-50 inline-block px-3 py-1.5 rounded-lg border border-red-100">
                        {decodeURIComponent(reason)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <button
                onClick={handleRetryPayment}
                className="w-full bg-gradient-to-r from-[#1c4980] to-[#2a68b5] text-white font-semibold py-4 px-6 rounded-xl hover:shadow-lg hover:shadow-blue-200 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center space-x-2 group"
              >
                <span>Try Again Securely</span>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </button>

              <button
                onClick={handleContinueShopping}
                className="w-full bg-white text-gray-700 font-medium py-4 px-6 rounded-xl border-2 border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors duration-200"
              >
                Return to Shop
              </button>
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-2">Need help with your payment?</p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-1 text-sm font-medium text-[#1c4980] hover:text-[#2a68b5]"
              >
                <span>Contact our support team</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PhonePeFailedPage;
